// Bottom zone: the action rail (prototype renderActions). Phase 1: Dispatch, Call list, Note, Bookmark,
// Disposition. Phase 2: Park (P) and Send to… (X) when features.park / features.transfer are on.
// Phase 5 (features.deterrence): Talk-down (T: hold to talk, a tap or a click opens the drawer; LIVE while recording)
// and Deterrents (R; "active" while a relay is on), first, where the prototype has them.

import { Icon, type IconName } from '../components/Icon.tsx';
import { useNow } from '../hooks.ts';
import type { Ctl } from './ctl.ts';
import { deviceActive } from './devices.ts';
import { dur, str } from './util.ts';

function Act({ icon, label, sub, k, onClick, cls = '', disabled, title }: { icon: IconName; label: string; sub: string; k: string; onClick: () => void; cls?: string; disabled?: boolean; title?: string }) {
  return (
    <button type="button" className={`act ${cls}`} onClick={onClick} disabled={disabled} title={title} aria-keyshortcuts={k}>
      <Icon name={icon} />
      <span>
        <b>{label}</b>
        <small>
          {sub}
          <kbd>{k}</kbd>
        </small>
      </span>
    </button>
  );
}

/** Talk-down and Deterrents: their own component so only they tick while a relay is on or a clip records. */
function DeterActs({ c, ro, roTitle }: { c: Ctl; ro: boolean; roTitle?: string }) {
  const p = c.ptt;
  const busy = p.phase !== 'idle';
  const relayOn = c.devices.relays.some((r) => deviceActive(r));
  const now = useNow(busy || relayOn ? 500 : 60_000);
  const talked = c.ev.actions.some((a) => a.type === 'talk_down' && a.status === 'succeeded');
  const deterred = c.ev.actions.some((a) => a.type === 'relay' && a.status === 'succeeded' && a.params.action !== 'release');
  const anyOn = c.devices.relays.some((r) => deviceActive(r, now));
  const talkSub = p.phase === 'recording' ? `REC ${dur(now - (p.startedAt ?? now))}` : p.phase === 'opening' ? 'opening mic…' : p.phase === 'sending' ? 'playing…' : 'hold to talk';
  return (
    <>
      <Act
        icon="mic"
        label="Talk-down"
        sub={talkSub}
        k="T"
        cls={`${talked ? 'done' : ''}${p.phase === 'recording' ? ' live' : ''}`}
        onClick={() => c.openDrawer({ k: 'talkdown' })}
        disabled={ro}
        title={roTitle ?? 'Click for messages and speakers; hold T to talk'}
      />
      <Act icon="bolt" label="Deterrents" sub={anyOn ? 'active' : 'strobe · siren'} k="R" cls={`${deterred ? 'done' : ''}${anyOn ? ' live' : ''}`} onClick={() => c.openDrawer({ k: 'deter' })} disabled={ro} title={roTitle} />
    </>
  );
}

export function ActionRail({ c }: { c: Ctl | null }) {
  if (!c) return <footer className="za empty" aria-hidden="true" />;
  const ev = c.ev;
  const dispatched = ev.actions.some((a) => a.type === 'dispatch' && a.status === 'succeeded');
  const answered = ev.actions.some((a) => a.type === 'call' && str(a.result?.outcome) === 'answered');
  const ro = !c.canAct;
  const roTitle = ro ? 'Read-only: you do not own this event' : undefined;
  const f = c.features;
  return (
    <footer className="za" aria-label="Actions">
      {f.deterrence && <DeterActs c={c} ro={ro} roTitle={roTitle} />}
      <Act icon="shield" label="Dispatch" sub={dispatched ? 'dispatched' : 'police'} k="D" cls={dispatched ? 'done' : ''} onClick={() => c.openDrawer({ k: 'dispatch' })} disabled={ro} title={roTitle} />
      <Act icon="phone" label="Call list" sub={`${ev.callProcedure.length} contacts`} k="K" cls={answered ? 'done' : ''} onClick={() => c.setCtx('contacts')} />
      <Act icon="note" label="Note" sub="add to log" k="O" onClick={() => c.openDrawer({ k: 'note' })} disabled={ro} title={roTitle} />
      <Act icon="bookmark" label="Bookmark" sub="this moment" k="B" onClick={c.bookmark} disabled={ro || !c.view.cameraId} title={roTitle} />
      {f.park && <Act icon="park" label="Park" sub={f.agent ? 'AI watch' : 'frees a slot'} k="P" onClick={() => c.openDrawer({ k: 'park' })} disabled={ro} title={roTitle} />}
      {f.transfer && <Act icon="send" label="Send to…" sub={c.isSup ? 'send · reassign' : 'operator · group'} k="X" onClick={() => c.openDrawer({ k: 'transfer' })} disabled={ro} title={roTitle} />}
      <Act icon="check" label="Disposition" sub="close event" k="C" cls="close" onClick={() => c.openDrawer({ k: 'disposition' })} disabled={ro} title={roTitle} />
    </footer>
  );
}
